import { materialsWarning } from "@roof-hawk/core";
import { useState } from "react";

import { t } from "../../i18n";
import { useAsync } from "../../lib/hooks";
import { dismissMaterialsCheck, fetchMaterialsCheck } from "../../lib/queries";

/**
 * A material order line that doesn't show what a note, email or text asks
 * for, as Jev found it (ADR 0234). The line and the message side by side, so
 * whoever reads it can judge in one look. A heads-up only: it never blocks,
 * and "Dismiss" hides it until the orders or the messages change. When it
 * speaks is `materialsWarning`'s decision, in packages/core.
 */
export function MaterialsCheckWarning({ jobId }: { jobId: string }) {
  const check = useAsync(() => fetchMaterialsCheck(jobId), [jobId]);
  const [error, setError] = useState<string | null>(null);
  const warning = materialsWarning(check.data);
  if (!warning) return null;

  return (
    <div className="cowarn matcheck" role="status">
      <p className="matcheck-title">{t(warning.title)}</p>
      {warning.summary ? (
        <p className="matcheck-what">{warning.summary}</p>
      ) : null}
      <div className="matcheck-pair">
        <div>
          <span className="matcheck-label">{t(warning.orderLabel)}</span>
          <span>{warning.line}</span>
        </div>
        <div>
          <span className="matcheck-label">
            {warning.source ?? t("Message")}
          </span>
          <span>&ldquo;{warning.quote}&rdquo;</span>
        </div>
      </div>
      <p className="matcheck-foot">
        <button
          type="button"
          className="btn-small"
          onClick={() => {
            setError(null);
            void dismissMaterialsCheck(jobId)
              .then(() => check.reload())
              .catch((e: unknown) =>
                setError(e instanceof Error ? e.message : String(e)),
              );
          }}
        >
          {t("Dismiss")}
        </button>
        {error ? <span role="alert"> {error}</span> : null}
      </p>
    </div>
  );
}
